import React from 'react';
import { Link } from 'react-router-dom';
import { cssVar } from '../../hooks/useThemeStyles';
import { useResponsive } from '../../hooks/useResponsive';

/**
 * Footer Component
 *
 * Global site footer with primary navigation, data source attribution,
 * and copyright. Rendered below the app's main <Routes>.
 *
 * IA notes:
 *  - Provides discoverable links to About, Best Snow, Activities, and Favorites
 *    pages that are otherwise orphaned in navigation.
 *  - Data-sources line improves crawler/user understanding of where snow
 *    measurements come from.
 */
const Footer = () => {
  const { isMobile } = useResponsive();
  const year = new Date().getFullYear();

  const linkStyle = {
    color: cssVar.textSecondary,
    textDecoration: 'none',
    fontSize: cssVar.textSm,
    fontWeight: '500',
    padding: '4px 0',
    transition: cssVar.transitionFast,
  };

  const sectionHeadingStyle = {
    margin: '0 0 8px 0',
    fontSize: cssVar.textXs,
    fontWeight: '700',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: cssVar.textMuted,
    fontFamily: cssVar.fontHeading,
  };

  return (
    <footer
      role="contentinfo"
      aria-label="Site footer"
      style={{
        borderTop: `1px solid ${cssVar.borderLight}`,
        background: cssVar.bgPrimary,
        color: cssVar.textSecondary,
        padding: isMobile ? '32px 20px 24px' : '48px 40px 32px',
        marginTop: 'auto',
      }}
    >
      <div
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '28px' : '48px',
          alignItems: isMobile ? 'flex-start' : 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        {/* Brand + tagline */}
        <div style={{ flex: '1 1 240px', maxWidth: '340px' }}>
          <div
            style={{
              fontSize: cssVar.textBase,
              fontWeight: '700',
              fontFamily: cssVar.fontHeading,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: cssVar.textPrimary,
              marginBottom: '8px',
            }}
          >
            Snowfield AI
          </div>
          <p
            style={{
              margin: '0 0 12px 0',
              fontSize: cssVar.textSm,
              color: cssVar.textMuted,
              lineHeight: 1.5,
            }}
          >
            Real-time ski resort snow forecasts powered by measured data from
            government and resort weather stations.
          </p>
          <div style={{ fontSize: cssVar.textXs, color: cssVar.textDisabled }}>
            &copy; {year} Snowfield AI
          </div>
        </div>

        {/* Explore links */}
        <nav
          aria-label="Footer navigation"
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '24px' : '48px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <h2 style={sectionHeadingStyle}>Explore</h2>
            <Link to="/" style={linkStyle}>
              All Resorts
            </Link>
            <Link to="/best-snow" style={linkStyle}>
              Best Snow
            </Link>
            <Link to="/activities" style={linkStyle}>
              Activities
            </Link>
            <Link to="/favorites" style={linkStyle}>
              Favorites
            </Link>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <h2 style={sectionHeadingStyle}>About</h2>
            <Link to="/about" style={linkStyle}>
              About Snowfield
            </Link>
            <Link to="/backcountry" style={linkStyle}>
              Backcountry
            </Link>
            <Link to="/heli" style={linkStyle}>
              Heli Skiing
            </Link>
          </div>
        </nav>
      </div>

      {/* Data sources line */}
      <div
        style={{
          maxWidth: '1400px',
          margin: '32px auto 0',
          paddingTop: '20px',
          borderTop: `1px solid ${cssVar.borderLight}`,
          fontSize: cssVar.textXs,
          color: cssVar.textMuted,
          lineHeight: 1.5,
        }}
      >
        Data sources: NWS, SNOTEL, SnoCountry, BC ASWS, SLF IMIS, Open-Meteo.{' '}
        <Link
          to="/about"
          style={{ color: cssVar.textSecondary, textDecoration: 'underline' }}
        >
          Learn more
        </Link>
        .
      </div>
    </footer>
  );
};

export default Footer;
